import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Landmark, BarChart3, FileSearch, Banknote, Calculator, FileCheck, FileText, Building, Briefcase, ArrowRight, CheckCircle2 } from 'lucide-react';
import SEO, { generateServiceSchema } from '@/components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const FinanceLoansPage = () => {
  const { locale } = useParams();

  const services = [
    { 
      icon: FileText, 
      title: 'Project Reports & DPR', 
      description: 'Comprehensive project reports and Detailed Project Reports (DPR) for bank loans, government schemes, and investor presentations.',
      items: ['Bank loan project reports', 'DPR for MSME/industrial projects', 'Techno-economic feasibility studies', 'CMA data preparation', 'Business plan documentation']
    },
    { 
      icon: Banknote, 
      title: 'Loan Facilitation & Syndication', 
      description: 'End-to-end support for securing term loans, working capital, and project finance from banks and financial institutions.',
      items: ['Term loan facilitation', 'Working capital finance', 'Project finance structuring', 'MSME & SME lending support', 'Equipment & machinery finance']
    },
    { 
      icon: Building, 
      title: 'Government Subsidies & Incentives', 
      description: 'Expert guidance on availing government subsidies, incentives, and schemes for industrial and business growth.',
      items: ['State & central government subsidies', 'MSME scheme benefits', 'Export promotion incentives', 'Technology upgradation schemes', 'Startup India benefits']
    },
    { 
      icon: Calculator, 
      title: 'Financial Accounting & Compliance', 
      description: 'Professional accounting services ensuring statutory compliance and financial transparency.',
      items: ['Corporate accounting', 'Statutory compliance', 'Financial statement preparation', 'MIS reporting', 'Internal audit support']
    },
    { 
      icon: FileSearch, 
      title: 'Taxation & Advisory', 
      description: 'Comprehensive tax planning, compliance, and advisory services for businesses and individuals.',
      items: ['Direct & indirect tax compliance', 'GST registration & filing', 'Tax planning & optimization', 'Income tax returns', 'Tax assessment support']
    },
    { 
      icon: Briefcase, 
      title: 'Business Setup & Registration', 
      description: 'Complete support for company incorporation, registrations, and regulatory approvals.',
      items: ['Company incorporation', 'MSME/Udyam registration', 'GST & PAN registration', 'Import-export code (IEC)', 'Factory & shop act licenses']
    },
  ];

  const industries = [
    'Manufacturing & Industrial',
    'Pharmaceuticals',
    'Food Processing',
    'Textiles & Apparel',
    'Engineering & Auto',
    'Chemicals',
    'Startups & MSMEs',
    'Export-Oriented Units'
  ];

  const whyChooseUs = [
    { title: 'End-to-End Support', desc: 'From project conceptualization to loan disbursement, we handle everything' },
    { title: 'Banking Relationships', desc: 'Strong ties with nationalized banks, private banks, and NBFCs' },
    { title: 'Government Liaison', desc: 'Expertise in navigating subsidy schemes and government processes' },
    { title: 'Industry Experience', desc: '25+ years of experience across manufacturing and industrial sectors' }
  ];

  const stats = [
    { value: '₹500+ Cr', label: 'Loans Facilitated' },
    { value: '200+', label: 'Projects Funded' },
    { value: '95%', label: 'Approval Rate' },
    { value: '50+', label: 'Banking Partners' }
  ];

  const serviceSchema = generateServiceSchema({
    type: 'Financial Consulting',
    name: 'Finance & Loans',
    description: 'Comprehensive financial consulting including project reports, loan facilitation, government subsidies, taxation, and business registration services.'
  });

  return (
    <div className="pt-20" data-testid="finance-loans-page">
      <SEO 
        title="Finance & Loans"
        description="Comprehensive financial consulting including project reports, DPR, loan facilitation, government subsidies, taxation, accounting, and business registration services for MSMEs and industries."
        path="/services/finance-loans"
        structuredData={serviceSchema}
      />

      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-white via-light-grey to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-teal/5" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <span className="inline-flex items-center px-4 py-2 bg-teal/10 text-teal rounded-full text-sm font-medium mb-6">
                <Landmark className="w-4 h-4 mr-2" />
                Financial Consulting
              </span>
              <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy leading-tight mb-6">
                Finance & Loans
              </h1>
              <p className="text-lg text-mid-grey leading-relaxed mb-8">
                Comprehensive financial consulting services to fuel your business growth. From project reports and loan facilitation to government subsidies and tax compliance — we provide end-to-end support for MSMEs, startups, and industrial enterprises.
              </p>
              <Link to={`/${locale}/contact`} className="btn-primary">
                Get Financial Advisory
              </Link>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="hidden lg:block">
              <img 
                src="/images/external/photo-1554224155-8d04cb21cd6c.jpg"
                alt="Financial analysis and consulting"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-navy">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.value}</div>
                <p className="text-white/70 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">Our Services</motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">Comprehensive Financial Solutions</motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <motion.div key={service.title} variants={fadeInUp} className="bg-light-grey p-6 rounded-xl hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-teal" />
                  </div>
                  <h3 className="font-bold text-navy text-lg mb-2">{service.title}</h3>
                  <p className="text-mid-grey text-sm mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-start text-sm text-mid-grey">
                        <CheckCircle2 className="w-4 h-4 text-teal mr-2 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-light-grey">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-12">
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">Our Advantage</motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">Why Choose H&G Finance</motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl text-center"
              >
                <div className="w-12 h-12 bg-teal text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  {index + 1}
                </div>
                <h4 className="font-bold text-navy mb-2">{item.title}</h4>
                <p className="text-mid-grey text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="font-playfair text-2xl text-navy mb-8 text-center">Industries We Serve</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {industries.map((industry) => (
              <span key={industry} className="px-6 py-3 bg-light-grey rounded-full text-navy font-medium text-sm hover:bg-teal hover:text-white transition-colors cursor-pointer">
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-navy">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-white text-2xl font-bold mb-6">Our Process</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['Initial Consultation', 'Document Collection', 'Report Preparation', 'Bank Liaison', 'Sanction Support', 'Disbursement', 'Subsidy Filing', 'Compliance'].map((step, index) => (
              <div key={step} className="bg-white/10 px-4 py-3 rounded-lg">
                <span className="text-white text-sm">{index + 1}. {step}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-teal">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-playfair text-4xl text-white mb-6">Ready to Fund Your Growth?</h2>
          <p className="text-white/80 text-lg mb-8">Connect with our financial consultants to explore funding options and government incentives for your business.</p>
          <Link to={`/${locale}/contact`} className="btn-primary inline-flex items-center">
            Schedule a Consultation <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FinanceLoansPage;
